import { z } from 'zod';

/**
 * WHAT A MEMBERSHIP INCLUDES, AND THE ONE SUM THAT TURNS IT INTO MONEY.
 *
 * `docs/PRICING.md` gives one rule that this module exists to make structurally true:
 * **entitlements key off the plan's features, never off founding status and never off price.** The
 * first `if (founding)` beside a feature gate is the moment "founding" silently becomes a feature
 * flag that can never be redefined — so a founding perk gets its own boolean here, and a limit that
 * differs between plans is a number here.
 *
 * Pure and shared, because three places have to agree about it: the repository that refuses or
 * bills, the director's screens that tell her before she is charged, and the public page that
 * describes the offer. A limit the site advertises and the server enforces differently is the same
 * class of bug as a price written down twice.
 *
 * ⚠ THE LADDER IS NEVER STORED. Three leagues included and $5 a month beyond it produce
 * $29.99 / $34.99 / $39.99 / $44.99 and so on, and none of those figures is written down anywhere.
 * `billableExtras` and the plans table are the whole mechanism. A table of rungs in code would go
 * stale the first time either number moved, and it would go stale silently.
 */

/**
 * `null` MEANS UNLIMITED, AND IT IS NOT THE SAME AS ZERO.
 *
 * Zero would be a plan that includes no leagues at all — a real thing somebody might one day sell,
 * and a catastrophe if it were how "no limit" were spelled. The distinction is worth the nullable.
 */
export const planFeaturesSchema = z.object({
  /** How many leagues she may run at once before the extras charge starts. `null` is unlimited. */
  includedLeagues: z.number().int().min(0).nullable().default(null),
  unlimitedSessions: z.boolean().default(true),
  unlimitedPlayers: z.boolean().default(true),
  directorGuidebook: z.boolean().default(false),
  directorsGroup: z.boolean().default(false),
  /** The standing seat on the monthly roundtable — a founding perk, and an explicit flag. */
  foundingRoundtable: z.boolean().default(false),
  /** Eligibility for one planning call; per-Director grants are recorded separately. */
  foundingPlanningCall: z.boolean().default(false),
});

export type PlanFeatures = z.infer<typeof planFeaturesSchema>;

/**
 * Read a plan's `features` jsonb without trusting it.
 *
 * It is a jsonb column written by earlier versions of this platform, so a row may predate any field
 * added later. Parsing with defaults means an old row resolves to "unlimited leagues" rather than to
 * a crash or, much worse, to `includedLeagues: undefined` arriving at a comparison and quietly
 * behaving like zero.
 */
export function readPlanFeatures(features: unknown): PlanFeatures {
  const parsed = planFeaturesSchema.safeParse(features ?? {});
  return parsed.success ? parsed.data : planFeaturesSchema.parse({});
}

/**
 * HOW MANY LEAGUES SHE IS CHARGED FOR, which is not how many she runs.
 *
 * The count passed in is every league she has not finished — draft, taking registrations, and in
 * play. A completed or archived league is free forever, so a director in her third year is charged
 * for what she is running now and never for her history.
 *
 * Never negative: a director under her allowance is not owed money, she is simply not charged.
 */
export function billableExtras(activeLeagues: number, includedLeagues: number | null): number {
  if (includedLeagues === null) return 0;
  return Math.max(0, activeLeagues - includedLeagues);
}

/**
 * What the extras cost, in cents, for a given count and unit price.
 *
 * Trivial arithmetic, and it lives here so that the screens, the server and any future invoice line
 * cannot each write their own version of it — which is how one of them ends up rounding.
 */
export function extrasCostCents(extras: number, unitAmountCents: number): number {
  return Math.max(0, extras) * unitAmountCents;
}

/** Technical input bound, not a marketed league limit. Keeps quantities and totals bounded. */
export const MAX_LEAGUE_ALLOWANCE = 1000;
export const leagueAllowanceSchema = z.number().int().min(1).max(MAX_LEAGUE_ALLOWANCE);

/** A free trial from a commissioner's code (owner, 2026-11-01). Not a full season: one league, ten days. */
export const TRIAL_DAYS = 10;
export const TRIAL_LEAGUES = 1;
